import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

import { Rules } from 'utils/constants';

import {
	DateField,
	FormItem,
	PhoneField,
	SelectField,
	SubmitButton,
	TextField,
} from 'componets/form';
import { Paper } from 'componets/ui';

import { GENDER_OPTIONS, PersonalInformationProps } from './index';

import styles from '../../index.module.scss';
import {useAppSelector} from "../../../../hooks";
import {selectCountries} from "../../../../selectors";
import {AppSelector} from "../../../../store";

export const PersonalInformationCompany: FC<PersonalInformationProps> = ({
	maxDate,
	onChangeSelect,
	countriesOptions,
	country,
	loading,
	isShowEconomicActivityOther = false
}) => {
	const { t } = useTranslation();
	const countries = useAppSelector(selectCountries);
	const { economicActivity = [] } = useAppSelector((state: AppSelector) => state.settings.globals);
	const citizenshipList =  countries?.filter((item) => item.activeCitizenship).map((item) => {
		return {
			label: item.label,
			value: item.id,
		};
	});
	const economicActivityList =  economicActivity?.map((item) => ({
		label: t(item.title),
		value: item.id,
	}))
	const formCompanyFields = [
		{
			name: 'companyName',
			component: <TextField label={t('companyNameText')} />,
			rules: [
				Rules.REQUIRED,
				Rules.MAX_LENGTH('companyName', 100),
				Rules.MIN_LENGTH('idNumber', 3),
			],
		},
		{
			name: 'idNumber',
			component: <TextField label={t('companyRegistrationNumberText')} />,
			rules: [Rules.REQUIRED, Rules.MAX_LENGTH('idNumber', 255), Rules.MIN_LENGTH('idNumber', 3)],
		},
		{
			name: 'countryId',
			component: (
				<SelectField
					selectText={t('selectCountryText')}
					options={countriesOptions}
					placeholder={t('countryRegistrationText')}
					onChange={onChangeSelect}
					isFilter
				/>
			),
			rules: [Rules.REQUIRED],
		},
		{
			name: 'city',
			component: <TextField label={t('cityText')} />,
			rules: [Rules.REQUIRED, Rules.MAX_LENGTH('city', 50)],
		},
		{
			name: 'address',
			component: <TextField label={t('addressLabelText')} />,
			rules: [Rules.REQUIRED, Rules.MAX_LENGTH('address', 255)],
		},
		{
			name: 'zip',
			component: <TextField label={t('zipCodeText')} />,
			rules: [Rules.REQUIRED, Rules.MAX_LENGTH('zip', 20)],
		},
		{
			name: 'economicActivity',
			component: (
				<SelectField
					options={economicActivityList}
					placeholder={t('economicActivityText')}
					selectText={t('selectEconomicActivityText')}
				/>
			),
			rules: [Rules.REQUIRED_SELECT],
		},
		...(isShowEconomicActivityOther ? [{
			name: 'otherEconomicActivity',
			component: <TextField label={t('economicActivityOther')}/>,
			rules: [Rules.REQUIRED, Rules.MAX_LENGTH('otherEconomicActivity', 48)],
		}] : []),
	];
	const formFields = [
		{
			name: 'firstName',
			component: <TextField label={t('firstNameText')} />,
			rules: [Rules.REQUIRED, Rules.MAX_LENGTH('firstName', 100)],
		},
		{
			name: 'lastName',
			component: <TextField label={t('lastNameText')} />,
			rules: [Rules.REQUIRED, Rules.MAX_LENGTH('lastName', 100)],
		},
		{
			name: 'citizenshipId',
			component: (
				<SelectField
					options={citizenshipList}
					placeholder={t('citizenshipText')}
					selectText={t('selectCitizenshipText')}
				/>
			),
			rules: [Rules.REQUIRED_SELECT],
		},
		{
			name: 'dateOfBirth',
			component: <DateField maxDate={maxDate} label={t('dateOfBirthText')} />,
			rules: [Rules.REQUIRED, Rules.INVALID_DATE(), Rules.MIN_AGE_18()],
		},
		{
			name: 'gender',
			component: (
				<SelectField
					options={GENDER_OPTIONS}
					placeholder={t('genderText')}
					selectText={t('selectGenderText')}
				/>
			),
			rules: [Rules.REQUIRED],
		},
		{
			name: 'phone',
			component: <PhoneField country={country} label="phoneMobileText" />,
			rules: [Rules.REQUIRED, Rules.PHONE('phone')],
		},
		{
			name: 'referralCode',
			component: <TextField label={t('referralCodeText')} />,
			rules: [Rules.MAX_LENGTH('referralCode', 255)],
		},
	];

	return (
		<>
			<Paper styleName={styles.blockForm} top={<>2 / 2</>}>
				<Typography.Title level={4} className={styles.contactInfo}>
					{t('companyInformationFormText')}
				</Typography.Title>

				{formCompanyFields.map(({ name, component, rules }) => (
					<FormItem key={name} name={name} rules={rules}>
						{component}
					</FormItem>
				))}
			</Paper>

			<Paper styleName={styles.blockForm}>
				<Typography.Title level={4} className={styles.contactInfo}>
					{t('companyRepresentativeText')}
				</Typography.Title>

				{formFields.map(({ name, component, rules }) => (
					<FormItem key={name} name={name} rules={rules}>
						{component}
					</FormItem>
				))}

				<SubmitButton text={t('continueText')} loading={loading} />
			</Paper>
		</>
	);
};
